import { PhoneDisconnectIcon } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { Button } from "@/components/ui/button";
import { usePublicTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import type { VoicePhase } from "../../hooks/use-web-audio";
import { SendButton } from "./ChatInput";

// Five frequency bands, one braided loop each — the HeyUp stripe palette, lows to
// highs. The colours are the brand tokens themselves, read off the page when the ring
// starts (a canvas cannot take `var()`): the hand-copied hexes they replace had
// drifted a shade off every one of them. `fallback` is only for a page without them.
const BANDS = [
  { f0: 20, f1: 120, token: "--orange", fallback: "#FFA415" },
  { f0: 120, f1: 400, token: "--pink", fallback: "#F13FAA" },
  { f0: 400, f1: 1200, token: "--magenta", fallback: "#C345E9" },
  { f0: 1200, f1: 3500, token: "--purple", fallback: "#9076F7" },
  { f0: 3500, f1: 9000, token: "--blue", fallback: "#57BCFF" },
];
const N = BANDS.length;

/**
 * Every number the look depends on, in one object: these were tuned on a standalone
 * bench (same maths, sliders on top) rather than guessed here.
 *
 *  - rotation is in turns/second and separates the three turn-taking cases: nothing
 *    sent yet (VAD off or on) barely turns, a turn handed to the LLM turns fast, and
 *    while the agent speaks rotation steps back so the frequencies carry the motion;
 *  - a single time constant (`rotTau`) smooths every speed change, so a phase flip
 *    can no longer jump the animation;
 *  - amplitudes are per-band normalised and softly kneed instead of clipped, which is
 *    what stops every thread pinning wide open as soon as she speaks;
 *  - the braid's INNER radius is fixed: only the wave amplitude changes, and each loop's
 *    radius is pushed out by exactly that amplitude, so the threads bloom outward as she
 *    speaks instead of eating into the middle of the drawing. (It used to be the other
 *    way round — outer edge pinned, threads converging inward — which reads as the logo
 *    closing up on the loudest words.) `ampSpan` is what that swell costs in room: at
 *    full level the outer edge reaches `rInt + 2 × (ampBase + ampSpan) × R0`, and it has
 *    to stay inside the canvas above the centre — see `centerY`.
 */
const RING = {
  rotIdle: 0.012,
  rotListening: 0.03,
  rotThinking: 0.24,
  rotSpeaking: 0.07,
  rotTau: 0.7,
  gate: 0.16,
  tilt: 0.45,
  agc: 0.65,
  agcFall: 2.2,
  gain: 1.1,
  knee: 1.7,
  spread: 0.7,
  attack: 0.05,
  release: 0.24,
  breath: 0.09,
  breathDepth: 0.45,
  breathPeriod: 5.5,
  micHint: 0.3,
  // geometry
  logoScale: 0.78, // the braid is a logo, not a backdrop: a share of the shorter side
  centerY: 0.42, // sits above the middle; the fade takes the space below
  radiusOuter: 0.9,
  lobes: 3,
  thickness: 13,
  sheath: 0.3,
  phaseSpread: 1,
  ampBase: 0.06,
  // Lowered with the switch to an outward swell: the wave now grows *away* from the
  // centre, so this is headroom above the resting braid rather than depth inside it.
  ampSpan: 0.18,
  loosen: 0.06,
  loosenTau: 0.85,
  steps: 140,
} as const;

const bandAverage = (
  data: Uint8Array,
  sampleRate: number,
  f0: number,
  f1: number,
) => {
  const perBin = sampleRate / 2 / data.length;
  const a = Math.max(1, Math.floor(f0 / perBin));
  const b = Math.min(data.length - 1, Math.ceil(f1 / perBin));
  let sum = 0;
  for (let i = a; i <= b; i++) sum += data[i];
  return sum / ((b - a + 1) * 255);
};

/** Frame-rate independent exponential approach — no `dt * 60` fudge factor. */
const approach = (current: number, target: number, tau: number, dt: number) =>
  target + (current - target) * Math.exp(-dt / Math.max(1e-4, tau));

const softKnee = (x: number, k: number) => Math.tanh(k * x) / Math.tanh(k);

interface VoiceRingProps {
  agentAnalyserRef: RefObject<AnalyserNode | null>;
  /** The caller's own mic, so the braid breathes with their voice while VAD hears them. */
  micAnalyserRef?: RefObject<AnalyserNode | null>;
  phaseRef: RefObject<VoicePhase>;
}

/** How far back through the thread counts as "reading history" (px). */
const SCROLL_BACK_PX = 140;

/**
 * The voice-mode centrepiece, laid over the live conversation: the ring sits on the
 * chat's own surface (so it follows light/dark and any branding), and that surface
 * fades out downward so the last messages stay readable underneath.
 */
export function VoiceRing({
  agentAnalyserRef,
  micAnalyserRef,
  phaseRef,
}: VoiceRingProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const groundRef = useRef<HTMLDivElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // Scrolling back through the conversation pins the ring small at the top, so the
  // thread gets the room instead.
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const viewport = wrapRef.current?.parentElement?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!viewport) return;
    let raf = 0;
    const read = () => {
      raf = 0;
      const fromBottom =
        viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      setCollapsed(fromBottom > SCROLL_BACK_PX);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(read);
    };
    viewport.addEventListener("scroll", onScroll, { passive: true });
    read();
    return () => {
      viewport.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ground = groundRef.current;
    if (!canvas || !ground) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const tokens = getComputedStyle(ground);
    const colors = BANDS.map(
      (band) => tokens.getPropertyValue(band.token).trim() || band.fallback,
    );

    const levels = new Float32Array(N);
    const target = new Float32Array(N);
    const peak = new Float32Array(N).fill(0.25);
    const freqData = new Uint8Array(1024);
    let micSmoothed = 0;

    // Geometry buffers reused between frames.
    const PAS = RING.steps;
    const SEG = N * PAS;
    const sX1 = new Float32Array(SEG);
    const sY1 = new Float32Array(SEG);
    const sX2 = new Float32Array(SEG);
    const sY2 = new Float32Array(SEG);
    const sZ = new Float32Array(SEG);
    const sI = new Uint8Array(SEG);
    const order = Array.from({ length: SEG }, (_, k) => k);

    let L = 0;
    let H = 0;
    let S = 0;
    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const r = canvas.getBoundingClientRect();
      L = r.width;
      H = r.height;
      // Radius, wave amplitude and thread thickness all derive from S, so scaling it
      // here resizes the logo as a whole instead of just thinning the braid.
      S = Math.min(L, H) * RING.logoScale;
      canvas.width = Math.round(L * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();

    // The braid is drawn by overdrawing the under-thread in the exact background
    // colour, so it has to be the *chat's* colour — read it off the ground layer
    // (which carries the panel's `bg-background`) rather than hardcoding a dark hex.
    let fond = "#ffffff";
    let fondReadAt = 0;
    const readFond = (now: number) => {
      if (now - fondReadAt < 500) return;
      fondReadAt = now;
      const bg = getComputedStyle(ground).backgroundColor;
      if (bg && bg !== "rgba(0, 0, 0, 0)") fond = bg;
    };

    /**
     * Agent bands → amplitude targets. Differences from the first version, all aimed
     * at "everything saturates wide open and only one thread moves":
     *  - the fixed gain ladder (1 + i × 0.55) becomes a tunable treble tilt *plus* a
     *    per-band running-peak normalisation, so no band lives at its ceiling;
     *  - the hard `min(1, …)` becomes a tanh knee, so loud passages compress instead
     *    of flat-topping;
     *  - `spread` mixes each band with the overall level, which is what makes the
     *    five threads move separately.
     */
    const readAgentBands = (dt: number) => {
      const analyser = agentAnalyserRef.current;
      if (!analyser) return target.fill(0);
      analyser.getByteFrequencyData(freqData);
      const sr = analyser.context.sampleRate;
      const fall = Math.exp(-dt / RING.agcFall);
      let sum = 0;
      const raw = new Array<number>(N);
      for (let i = 0; i < N; i++) {
        let v = bandAverage(freqData, sr, BANDS[i].f0, BANDS[i].f1);
        v = Math.max(0, v - RING.gate) / (1 - RING.gate);
        v *= 1 + RING.tilt * (i / (N - 1));
        peak[i] = Math.max(v, peak[i] * fall, 0.05);
        v = v * (1 - RING.agc) + (v / peak[i]) * RING.agc;
        raw[i] = v * RING.gain;
        sum += raw[i];
      }
      const mean = sum / N;
      for (let i = 0; i < N; i++) {
        const mixed = mean * (1 - RING.spread) + raw[i] * RING.spread;
        target[i] = softKnee(Math.max(0, mixed), RING.knee);
      }
    };

    /** Nothing audible: each thread breathes on its own period, never in lockstep. */
    const breathe = (clock: number, hint: number) => {
      for (let i = 0; i < N; i++) {
        const w = (Math.PI * 2) / (RING.breathPeriod * (1 + 0.18 * i));
        target[i] =
          RING.breath * (1 + RING.breathDepth * Math.sin(clock * w + i * 1.7)) +
          hint;
      }
    };

    /** Overall mic energy, 0..1 — the caller's own voice while VAD hears them. */
    const readMicLevel = () => {
      const analyser = micAnalyserRef?.current;
      if (!analyser) return 0;
      analyser.getByteFrequencyData(freqData);
      const n = Math.min(freqData.length, analyser.frequencyBinCount);
      let sum = 0;
      for (let i = 0; i < n; i++) sum += freqData[i];
      return Math.min(1, sum / (n * 255) / 0.25);
    };

    let angle = 0;
    let rotSpeed = 0;
    let loosen = 0;
    let clock = 0;
    let last = performance.now();
    let raf = 0;

    const frame = (nowMs: number) => {
      let dt = (nowMs - last) / 1000;
      last = nowMs;
      dt = Math.min(Math.max(dt, 0), 1 / 20);
      clock += dt;
      readFond(nowMs);

      const phase = phaseRef.current || "idle";

      const rotTarget =
        phase === "thinking"
          ? RING.rotThinking
          : phase === "speaking"
            ? RING.rotSpeaking
            : phase === "listening"
              ? RING.rotListening
              : RING.rotIdle;
      rotSpeed = approach(rotSpeed, rotTarget, RING.rotTau, dt);
      angle += rotSpeed * dt * Math.PI * 2 * (reduce ? 0.35 : 1);

      // The braid loosens while the turn is with the LLM — smoothly, not as a step.
      loosen = approach(
        loosen,
        phase === "thinking" ? 1 : 0,
        RING.loosenTau,
        dt,
      );

      if (phase === "speaking") {
        readAgentBands(dt);
      } else {
        // Smoothed here so a noisy per-frame mic reading doesn't make it jitter.
        micSmoothed = approach(micSmoothed, readMicLevel(), 0.18, dt);
        breathe(clock, phase === "listening" ? RING.micHint * micSmoothed : 0);
      }

      for (let i = 0; i < N; i++) {
        const tau = target[i] > levels[i] ? RING.attack : RING.release;
        levels[i] = approach(levels[i], target[i], tau, dt);
      }

      // --- draw ---
      const cx = L / 2;
      const cy = H * RING.centerY;
      const R0 = S * 0.4;
      const epaisseur = Math.max(1, RING.thickness * (S / 900));
      const gaine = epaisseur * (1 + RING.sheath);
      const base = R0 * RING.ampBase;
      // Inner edge of the braid, fixed bar the "thinking" loosening; each loop's own
      // radius is this plus its wave amplitude, so the threads swell outward from it.
      // Offset by the resting amplitude so silence looks exactly as it did when the
      // outer edge was the fixed one.
      const rInt = R0 * (RING.radiusOuter + loosen * RING.loosen) - 2 * base;
      const freq = R0 * RING.ampSpan;
      const DECAL = ((Math.PI * 2) / N) * RING.phaseSpread;

      ctx.clearRect(0, 0, L, H);

      let k = 0;
      for (let i = 0; i < N; i++) {
        const ampl = base + freq * levels[i];
        const rBoucle = rInt + ampl;
        const ph = i * DECAL + loosen * 0.15;
        let px = 0;
        let py = 0;
        for (let s = 0; s <= PAS; s++) {
          const aa = (s / PAS) * Math.PI * 2;
          const r = rBoucle + ampl * Math.sin(aa * RING.lobes + ph);
          const bb = aa + angle;
          const x = cx + Math.cos(bb) * r;
          const y = cy + Math.sin(bb) * r;
          if (s > 0) {
            sX1[k] = px;
            sY1[k] = py;
            sX2[k] = x;
            sY2[k] = y;
            sZ[k] = Math.cos((aa - Math.PI / PAS) * RING.lobes + ph);
            sI[k] = i;
            k++;
          }
          px = x;
          py = y;
        }
      }

      order.sort((p, q) => sZ[p] - sZ[q]);
      for (let n = 0; n < SEG; n++) {
        const key = order[n];
        const x1 = sX1[key];
        const y1 = sY1[key];
        const x2 = sX2[key];
        const y2 = sY2[key];
        if (gaine > epaisseur + 0.4) {
          ctx.strokeStyle = fond;
          ctx.lineCap = "butt";
          ctx.lineWidth = gaine;
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
        }
        ctx.strokeStyle = colors[sI[key]];
        ctx.lineCap = "round";
        ctx.lineWidth = epaisseur;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [agentAnalyserRef, micAnalyserRef, phaseRef]);

  return (
    // The whole composite (ring + its ground) fades out downward, so there is no hard
    // edge between the visual and the thread scrolling under it.
    <div
      ref={wrapRef}
      className={cn(
        "pointer-events-none absolute inset-x-0 top-0 z-10 transition-[height] duration-300 ease-out",
        collapsed ? "h-24" : "h-60",
      )}
      // The fade has to start *below* the braid, not across it: the ring's lowest point
      // sits around 80% of this box (centre at `centerY`, plus its radius), and starting
      // the gradient higher washed out the bottom of the logo itself.
      style={{
        padding: "20px",
        maskImage:
          "linear-gradient(to bottom, #000 0 80%, rgba(0,0,0,.5) 92%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(to bottom, #000 0 80%, rgba(0,0,0,.5) 92%, transparent 100%)",
      }}
      aria-hidden="true"
    >
      <div
        ref={groundRef}
        className="absolute inset-0 bg-background"
      />
      <canvas ref={canvasRef} className="relative block size-full" />
    </div>
  );
}

interface VoiceCallBarProps {
  onHangup: () => void;
  connecting: boolean;
  /** Send a message typed while on the call — it barges in on the agent. */
  onSendText?: (text: string) => void;
}

/** The composer while on a call: type to barge in, or hang up. */
export function VoiceCallBar({
  onHangup,
  connecting,
  onSendText,
}: VoiceCallBarProps) {
  const { t } = usePublicTranslation();
  const [text, setText] = useState("");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    onSendText?.(text.trim());
    setText("");
  };

  return (
    <div className="border-t bg-background/90 px-3 py-3 backdrop-blur">
      <div className="flex items-center gap-2">
        {onSendText && (
          <form
            onSubmit={submit}
            className="flex flex-1 items-center gap-1.5 rounded-[var(--chat-radius,1rem)] border bg-background py-1 pl-3 pr-1 transition-colors focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/15"
          >
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("chat.call_write")}
              aria-label={t("chat.call_write_label")}
              // 16px on a phone, for the composer's reason: Safari zooms the page in
              // on a smaller focused field, which inside the widget scrolls the host's
              // site sideways under the sheet.
              className="min-w-0 flex-1 bg-transparent px-1 text-base outline-none placeholder:text-muted-foreground sm:text-sm"
            />
            <SendButton
              type="submit"
              disabled={!text.trim()}
              className="size-8"
            />
          </form>
        )}
        <Button
          type="button"
          variant="destructive"
          size="icon"
          onClick={onHangup}
          title={t("chat.hangup")}
          aria-label={t("chat.hangup")}
          className="size-11 active:scale-95"
        >
          <PhoneDisconnectIcon className="size-5" weight="fill" />
        </Button>
      </div>
      {connecting && (
        <p className="mt-2 text-center text-xs text-muted-foreground">
          {t("chat.connecting")}
        </p>
      )}
    </div>
  );
}
